import { ApiTag } from '@/enum/apiTag'
import { Survey } from '@/types/survey'
import { NotificationSettings, User } from '@/types/user'
import { apiSlice } from '../slices/apiSlice'

const environment = import.meta.env.VITE_AZURE_ENVIRONMENT

export type UpdateAccountSettingsRequest = {
  adObjectId: string
  isPhoneValidated: boolean
  phoneNumber: string
}

export type CheckPhoneVerificationCodeResponse = {
  errors: string[] | null
  isValid: boolean
  sid: string
}

export type SendPhoneVerificationResponse = {
  errors: string[] | null
  isValid: boolean
  sid: string
}

export type CheckTwilioVerificationCasesResponse = {
  token: string
}

export const userApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getUser: build.query<User, string>({
      query: (id) => `/func-rts-b2c-user-management-${environment}/users/${id}`,
      transformResponse: (response: User[]) => response[0],
      providesTags: [{ type: ApiTag.User }],
    }),
    updateUser: build.mutation<User, User>({
      query: (body) => ({
        url: `/func-rts-b2c-user-management-${environment}/user/profile`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: ApiTag.User }],
    }),
    updateAccountSettings: build.mutation<User, UpdateAccountSettingsRequest>({
      query: (body) => ({
        url: `/func-rts-b2c-user-management-${environment}/user/account-settings`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: ApiTag.User }],
    }),
    updateNotificationSetting: build.mutation<
      User,
      { userId: string; settings: NotificationSettings }
    >({
      query: ({ userId, settings }) => ({
        url: `/func-rts-b2c-user-management-${environment}/user/notification-settings/${userId}`,
        method: 'PUT',
        body: settings,
      }),
      invalidatesTags: [{ type: ApiTag.User }],
    }),
    sendPhoneVerification: build.mutation<
      SendPhoneVerificationResponse,
      string
    >({
      query: (phoneNumber) => ({
        url: `/twilio-portal-api-${environment}/phoneverification`,
        method: 'POST',
        body: {
          PhoneNumberOrEmail: phoneNumber,
          channel: 'sms',
        },
        skipAuth: true,
      }),
    }),
    checkPhoneVerificationCode: build.mutation<
      CheckPhoneVerificationCodeResponse,
      { phoneNumber: string; code: string }
    >({
      query: ({ phoneNumber, code }) => ({
        url: `/twilio-portal-api-${environment}/checkphoneverificationcode`,
        method: 'POST',
        body: {
          PhoneNumberOrEmail: phoneNumber,
          channel: 'sms',
          code,
        },
        skipAuth: true,
      }),
    }),
    sendEmailVerification: build.mutation<
      SendPhoneVerificationResponse,
      string
    >({
      query: (email) => ({
        url: `/twilio-portal-api-${environment}/phoneverification`,
        method: 'POST',
        body: {
          PhoneNumberOrEmail: email,
          channel: 'email',
        },
        skipAuth: true,
      }),
    }),
    checkEmailVerificationCode: build.mutation<
      CheckPhoneVerificationCodeResponse,
      { email: string; code: string }
    >({
      query: ({ email, code }) => ({
        url: `/twilio-portal-api-${environment}/checkphoneverificationcode`,
        method: 'POST',
        body: {
          PhoneNumberOrEmail: email,
          channel: 'email',
          code,
        },
        skipAuth: true,
      }),
    }),
    checkTwilioVerificationCases: build.mutation<
      CheckTwilioVerificationCasesResponse,
      { email: string; code: string }
    >({
      query: ({ email, code }) => ({
        url: `/twilio-portal-api-${environment}/check-twilio-verification-cases`,
        method: 'POST',
        body: {
          PhoneNumberOrEmail: email,
          channel: 'email',
          code,
        },
        skipAuth: true,
      }),
    }),
    getSurveys: build.query<Survey[], void>({
      query: () => ({
        url: `/func-rts-b2c-user-management-${environment}/survey`,
        method: 'GET',
      }),
    }),
    sendPortalInquiryEmail: build.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: `/func-customerportal-sendgrid-${environment}/SendPortalInquiryEmail`,
        method: 'POST',
        body: {
          email,
        },
        skipAuth: true,
      }),
    }),
  }),
})

export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateAccountSettingsMutation,
  useUpdateNotificationSettingMutation,
  useSendPhoneVerificationMutation,
  useCheckPhoneVerificationCodeMutation,
  useSendEmailVerificationMutation,
  useCheckEmailVerificationCodeMutation,
  useGetSurveysQuery,
  useCheckTwilioVerificationCasesMutation,
  useSendPortalInquiryEmailMutation,
} = userApi
